<script lang="ts" setup>
import Konva from "konva";
import { computed, onMounted, Ref, ref, watch } from "vue";
import {
  usePerformAnimation,
  usePerformAnimationWithCanvas,
} from "@/components/mobile/generation/animation/usePerformAnimation";

// Props & Emit
const props = withDefaults(
  defineProps<{
    urls: string[];
    durationGetter?: (index: number) => number;
  }>(),
  {
    durationGetter: () => 1000,
  }
);

// States
const container = ref<HTMLDivElement | null>(null);
const stage = ref<Konva.Stage | null>(null);
const layer = ref<Konva.Layer | null>(null);
const keyFrameUrls = computed(() => props.urls);
const loading = ref(false);

const { play } = usePerformAnimationWithCanvas(
  stage as Ref<Konva.Stage>,
  layer as Ref<Konva.Layer>,
  keyFrameUrls,
  {
    durationGetter: props.durationGetter,
    draggable: false,
  }
);

// Lifecycle
const prepareKonva = async () => {
  loading.value = true;
  try {
    const { width, height } = container.value!.getBoundingClientRect();
    stage.value = new Konva.Stage({
      container: "animation-stage",
      width: width,
      height: height,
    });
    const animationLayer = new Konva.Layer();
    stage.value?.add(animationLayer);
    layer.value = animationLayer;
    await play();
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  prepareKonva();
});

watch(
  () => props.urls,
  () => {
    play();
  }
);
</script>
<template>
  <div class="relative">
    <div id="animation-stage" ref="container" class="w-full h-full"></div>
    <div
      v-if="loading"
      class="absolute left-0 right-0 bottom-0 top-0 flex items-center justify-center"
    >
      <span class="loading"></span>
    </div>
  </div>
</template>
